import { useState } from 'react';

import TableChartIcon from '@mui/icons-material/TableChart';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { Box, Typography, useTheme } from '@mui/material';

import { LEAGUES } from '@/constants/leagues';
import { obtenerEquipos } from '@/data/equipos';
import { getPlayerName, replaceDiacritis } from '@/helpers/players-helper';

import Placement1Component from '../ads-components/placements/placement1';
import { getCurrentSeason } from '../jugadores-v2/information/jugadores-additional-data';
import ShareArticleAdminComponent from '../page-components/noticias/share-article-admin';
import PlayerGeneralStatusCard from '../shared/player-general-status-card';

import EstadoJugadoresFilters from './EstadoJugadoresFilters';
import EstadoJugadoresNoHayJugadores from './EstadoJugadoresNoHayJugadores';
import JugadoresEstadoCard from './JugadoresEstadoCard';

const EstadoJugadoresComponent = ({ status, players, title, ruta, icon }) => {
    const [name, setName] = useState('');
    const teams = obtenerEquipos(LEAGUES.LA_LIGA, true);
    const [filterTeam, setFilterTeam] = useState('all');
    const [viewMode, setViewMode] = useState('table'); // 'table' o 'cards'
    const theme = useTheme();

    const onChangeSearchPlayer = (event) => {
        setName(event.target.value);
    };

    const onChangeSearchTeam = (event) => {
        setFilterTeam(event.target.value);
    };

    const toggleViewMode = (mode) => {
        setViewMode(mode);
    };

    const getFilteredPlayers = () => {
        let playersFiltered = [...players];

        if (filterTeam !== 'all') {
            playersFiltered = playersFiltered.filter((player) => player.teamId === parseInt(filterTeam));
        }

        if (name) {
            playersFiltered = playersFiltered.filter((player) =>
                replaceDiacritis(getPlayerName(player))?.includes(replaceDiacritis(name))
            );
        }

        return playersFiltered;
    };

    const getAllPlayers = () => {
        const filteredPlayers = getFilteredPlayers();

        if (!filteredPlayers.length) {
            return <EstadoJugadoresNoHayJugadores />;
        }

        if (viewMode === 'table') {
            return teams
                ?.filter((team) => filteredPlayers.some((player) => player.teamId === team.id))
                ?.map((team, index) => {
                    const teamPlayers = filteredPlayers.filter((player) => player.teamId === team.id);

                    return (
                        <Box key={index} className="team-general-status-card">
                            <Box
                                bgcolor={theme.palette.background.darkCard}
                                height={'30px'}
                                display={'flex'}
                                alignItems={'center'}
                                pl={1}
                            >
                                <Typography fontSize={'14px'}>{team.name}</Typography>
                            </Box>
                            {teamPlayers.map((player, idx) => (
                                <PlayerGeneralStatusCard
                                    key={idx}
                                    noticia={player}
                                    status={status}
                                    displayPositionForTeam={true}
                                />
                            ))}
                        </Box>
                    );
                });
        } else {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        borderRadius: '4px',
                        bgcolor: theme.palette.background.default,
                    }}
                >
                    {filteredPlayers
                        ?.sort((a, b) => a.teamId - b.teamId)
                        ?.map((player, index) => (
                            <JugadoresEstadoCard key={index} player={player} />
                        ))}
                </Box>
            );
        }
    };

    const getViewToggle = () => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '200px',
                height: '50px',
                position: 'relative',
                cursor: 'pointer',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '5px',
                    left: viewMode === 'table' ? '5px' : 'calc(50% + 5px)',
                    width: '90px',
                    height: '40px',
                    backgroundColor: '#1976d2',
                    borderRadius: '4px',
                    transition: 'left 0.3s ease',
                }}
            ></Box>

            {/* Vista Tabla */}
            <Box
                sx={{
                    zIndex: 2,
                    flex: 1,
                    textAlign: 'center',
                    color: viewMode === 'table' ? '#fff' : '#757575',
                    transition: 'color 0.3s ease',
                }}
                onClick={() => toggleViewMode('table')}
            >
                <TableChartIcon />
            </Box>

            {/* Vista Tarjetas */}
            <Box
                sx={{
                    zIndex: 2,
                    flex: 1,
                    textAlign: 'center',
                    color: viewMode === 'cards' ? '#fff' : '#757575',
                    transition: 'color 0.3s ease',
                }}
                onClick={() => toggleViewMode('cards')}
            >
                <ViewModuleIcon />
            </Box>
        </Box>
    );

    return (
        <>
            <Box className="container">
                <Typography variant="h1" color={theme.palette.text.primary}>
                    Jugadores {title} de La Liga {getCurrentSeason()}
                </Typography>

                <EstadoJugadoresFilters
                    onChangeSearchPlayer={onChangeSearchPlayer}
                    onChangeSearchTeam={onChangeSearchTeam}
                    name={name}
                    teams={teams}
                    filterTeam={filterTeam}
                />

                {getViewToggle()}

                <Placement1Component />

                <ShareArticleAdminComponent
                    title={`Consulta los jugadores ${title?.toLowerCase()} de La Liga`}
                    ruta={ruta}
                    icon={icon}
                />

                <Box className="all-team-players-container">{getAllPlayers()}</Box>
            </Box>
        </>
    );
};

export default EstadoJugadoresComponent;
